import { useLocation, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useConfig } from "@/hooks/use-config";
import { signIn } from "@/lib/auth-client";
import { DemoPanel } from "./DemoPanel";

interface LoginValues {
  email: string;
  password: string;
}

const SANS = "'Inter Tight', 'Helvetica Neue', Helvetica, Arial, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

function Spinner() {
  return (
    <span
      style={{
        width: 14,
        height: 14,
        border: "1.5px solid currentColor",
        borderRightColor: "transparent",
        borderRadius: "50%",
        display: "inline-block",
        animation: "usg-spin .7s linear infinite",
        flexShrink: 0,
      }}
    />
  );
}

export function LoginForm() {
  const router = useRouter();
  const location = useLocation();
  const { data: config } = useConfig();

  const isIdleExpiry = new URLSearchParams(location.search).get("reason") === "idle";
  const [authError, setAuthError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<LoginValues>({
    defaultValues: { email: "", password: "" },
  });

  function handleFillCredentials(email: string, password: string) {
    setValue("email", email);
    setValue("password", password);
  }

  async function onSubmit(values: LoginValues) {
    setAuthError(null);
    const { data, error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (error) {
      setAuthError("Couldn't sign in. Try again.");
      return;
    }

    const role = (data as { user?: { role?: string } } | null)?.user?.role;
    if (role === "ops") {
      router.navigate({ to: "/dashboard/settings/users" });
    } else {
      router.navigate({ to: "/dashboard" });
    }
  }

  return (
    <div style={{ fontFamily: SANS }}>
      <style>{`
        @keyframes usg-spin { to { transform: rotate(360deg); } }
        .usg-login-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(11,23,20,0.18);
          padding: 14px 0 12px;
          color: #0B1714;
          font: 500 16px/1.2 ${SANS};
          letter-spacing: -0.01em;
          outline: none;
          transition: border-color .18s ease;
          box-sizing: border-box;
        }
        .usg-login-input::placeholder { color: rgba(11,23,20,0.38); font-weight: 400; }
        .usg-login-input:focus { border-bottom-color: #4ED38B; }
      `}</style>

      {/* Session expiry banner */}
      {isIdleExpiry && (
        <div
          style={{
            background: "rgba(232,182,92,0.12)",
            border: "1px solid rgba(232,182,92,0.35)",
            borderRadius: 2,
            padding: "10px 14px",
            marginBottom: 24,
            font: `500 10.5px/1.4 ${MONO}`,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#8B6A1A",
          }}
        >
          Session expired — please sign in again.
        </div>
      )}

      {/* Heading block */}
      <div style={{ marginBottom: 36 }}>
        <div
          style={{
            font: `500 10.5px/1 ${MONO}`,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(11,23,20,0.50)",
            marginBottom: 16,
          }}
        >
          {"// Time tracking platform"}
        </div>
        <h1
          style={{
            font: `500 44px/1.02 ${SANS}`,
            letterSpacing: "-0.025em",
            color: "#0B1714",
            margin: "0 0 8px",
          }}
        >
          Sign in.
        </h1>
        <p
          style={{
            font: `400 15px/1.5 ${SANS}`,
            color: "rgba(11,23,20,0.62)",
            margin: 0,
            maxWidth: 360,
          }}
        >
          Continue to your work orders, hours, and crew schedule.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Email */}
        <div style={{ marginBottom: 24 }}>
          <label
            htmlFor="login-email"
            style={{
              display: "block",
              font: `500 10.5px/1 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(11,23,20,0.55)",
              marginBottom: 4,
            }}
          >
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            placeholder="you@urbansolutiongroup.com"
            className="usg-login-input"
            {...register("email")}
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 4,
            }}
          >
            <label
              htmlFor="login-password"
              style={{
                display: "block",
                font: `500 10.5px/1 ${MONO}`,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(11,23,20,0.55)",
              }}
            >
              Password
            </label>
            <button
              type="button"
              style={{
                background: "none",
                border: 0,
                padding: 0,
                font: `500 10.5px/1 ${MONO}`,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#1B6B47",
                cursor: "pointer",
              }}
            >
              Forgot →
            </button>
          </div>
          <input
            id="login-password"
            type="password"
            placeholder="••••••••••"
            className="usg-login-input"
            {...register("password")}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: "100%",
            background: "#1B6B47",
            color: "#F4F1EA",
            border: 0,
            padding: "16px 20px",
            font: `600 13px/1 ${SANS}`,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: isSubmitting ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            borderRadius: 2,
            transition: "background .15s ease, transform .15s ease",
            opacity: isSubmitting ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) (e.currentTarget as HTMLElement).style.background = "#236052";
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) (e.currentTarget as HTMLElement).style.background = "#1B6B47";
          }}
          onMouseDown={(e) => {
            if (!isSubmitting) (e.currentTarget as HTMLElement).style.transform = "translateY(1px)";
          }}
          onMouseUp={(e) => {
            if (!isSubmitting) (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
          }}
        >
          {isSubmitting ? (
            <>
              <Spinner /> Signing in
            </>
          ) : (
            <>
              Sign in <span style={{ opacity: 0.8 }}>→</span>
            </>
          )}
        </button>

        {/* Status row */}
        {authError && (
          <div
            style={{
              font: `500 10.5px/1.4 ${MONO}`,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#C8602F",
              marginTop: 12,
            }}
          >
            {authError}
          </div>
        )}
      </form>

      {/* Demo accounts toggle */}
      {config?.demoMode && (
        <div style={{ marginTop: 36 }}>
          <DemoPanel onFillCredentials={handleFillCredentials} />
        </div>
      )}
    </div>
  );
}
